import { Injectable } from '@angular/core';
import { DatadogConfig, Environment } from '../../../environments/environment';
import { BehaviorSubject } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import { datadogRum, TrackingConsent } from '@datadog/browser-rum';
import { DebugLogger } from '../../shared/utils/debugLogger';

@Injectable({
  providedIn: 'root'
})
export class DatadogService {
  private datadogLoaded$ = new BehaviorSubject<boolean>(false);

  constructor() {
    this.datadogLoaded$.next(false);
  }

  loadDatadog(cookiesEnabled: boolean) {
    try {
      datadogRum.init({
        applicationId: DatadogConfig?.applicationId,
        clientToken: DatadogConfig?.clientToken,
        site: 'datadoghq.eu',
        service: 'onlineservice-frontend',
        env: Environment.stage.toLowerCase(),
        version: DatadogConfig?.appVersion,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 100,
        trackResources: true,
        trackLongTasks: true,
        trackUserInteractions: true,
        trackViewsManually: true,
        trackingConsent: 'not-granted' as TrackingConsent,
        defaultPrivacyLevel: 'mask',
        workerUrl: '/datadog-worker/worker.js',
        allowedTracingUrls: [
          {
            match: /http(s?):\/\/privacy-service\..*\.gewkoelnag\.de/,
            propagatorTypes: ['tracecontext'],
          },
          {
            match: /http(s?):\/\/privacy(-dev)?\.rheinenergie\.com/,
            propagatorTypes: ['tracecontext'],
          },
          {
            match: /http(s?):\/\/.*\.execute-api\.eu-central-1\.amazonaws\.com/,
            propagatorTypes: ['tracecontext']
          },
        ]
      });
      DebugLogger.debug(this.constructor.name, 'loadDatadog()', `loading with cookies ${cookiesEnabled ? 'enabled' : 'disabled'}`);
      this.datadogLoaded$.next(true);
    } catch (error) {
      DebugLogger.error(this.constructor.name, 'loadDatadog()', error);
    }
  }

  public setConsent(allowed: boolean) {
    datadogRum.setTrackingConsent(allowed ? 'granted' : 'not-granted');
  }

  public trackCustomEvent(name: string) {
    this.datadogLoaded$.pipe(
      filter((isLoaded) => !!isLoaded),
      first()
    ).subscribe({
      next: () => {
        datadogRum.addAction(name);
      }
    });
  }

  public trackPageView(page: string) {
    // track pageView after datadog is loaded (delayed, because we have to wait for the user input of the cookie handling
    // to load datadog with the correct disableCookieUsage setting)
    this.datadogLoaded$.pipe(
      filter((isLoaded) => !!isLoaded),
      first()
    ).subscribe(
      {
        next: () => {
          DebugLogger.debug(this.constructor.name, 'trackPageView()', page);

          datadogRum.startView({
            name: page
          });
        },
        error: (error: unknown) => {
          DebugLogger.error(this.constructor.name, 'trackPageView()', error);
        }
      });
  }

}
